import useSelectionStore from '@store/useSelectionStore';
import { ClickableType, Team } from '@utils/enums';
import SpawnBeaconIcon from '@gameobjects/SpawnBeaconIcon';
import StatBar from '@components/ui/general/StatBar';
import { Shield, Swords } from 'lucide-react';
import './StructureInfoPanel.css';

export default function StructureInfoPanel() {
  const { currentClick } = useSelectionStore();

  // Only show if a structure is selected
  if (!currentClick || currentClick.type !== ClickableType.STRUCTURE) {
    return null;
  }

  const structure = currentClick.data;

  if (!structure) {
    throw new Error('[StructureInfoPanel] Structure data missing');
  }

  // Validate required structure data
  if (!structure.teamClass) {
    throw new Error(`[StructureInfoPanel] Structure missing teamClass`);
  }

  if (!structure.subtype) {
    throw new Error(`[StructureInfoPanel] Structure missing subtype`);
  }

  // Check for HP (structures should have HP)
  if (structure.hp === undefined) {
    console.warn(`[StructureInfoPanel] Structure ${structure.id} missing hp value`);
  }

  // Handle different structure subtypes
  switch (structure.subtype) {
    case 'spawn_beacon':
      return <SpawnBeaconPanel structure={structure} />;
    default:
      console.warn(`[StructureInfoPanel] Unknown structure subtype: ${structure.subtype}`);
      return (
        <div className="structure-info-card">
          <h3>Unknown Structure: {structure.subtype}</h3>
          <p>Position: ({structure.x}, {structure.y})</p>
        </div>
      );
  }
}

// Spawn Beacon specific panel
function SpawnBeaconPanel({ structure }) {
  // For capture mechanics (if structure has them)
  const hasCapture = structure.capture_required !== undefined;

  return (
    <div className="structure-info-card">
      <div className="structure-header">
        <SpawnBeaconIcon beacon={structure} size="big" />
        <h3 className="structure-title">Spawn Beacon</h3>
      </div>

      {/* Control Status */}
      <div className={`structure-control team-${structure.teamClass}`}>
        <Shield size={16} />
        <span>Controlled by {structure.teamClass === Team.OWN ? 'You' : structure.teamClass}</span>
      </div>

      {/* Capture Progress (if applicable) */}
      {hasCapture && structure.capturing_team_class && structure.capturing_team_class !== structure.teamClass && (
        <div className="structure-capture">
          <div className={`capturing-team team-${structure.capturing_team_class}`}>
            <Swords size={16} />
            <span>{structure.capturing_team_class === Team.OWN ? 'You are' : `${structure.capturing_team_class} is`} capturing!</span>
          </div>

          <StatBar
            value={structure.capture_points}
            maxValue={structure.capture_required}
            colorMode="team"
            teamClass={structure.capturing_team_class}
            textStrokeColor="#000"
          />
        </div>
      )}

      {/* Position */}
      <div className="structure-position">
        <span>Position: ({structure.x}, {structure.y})</span>
      </div>

      {/* Capture requirement if exists */}
      {hasCapture && (
        <div className="structure-stats">
          <span>Capture Required: {structure.capture_required} points</span>
        </div>
      )}
    </div>
  );
}